// The live channel (api.md §Live): a "cursor moved" nudge refetches what's on screen, so the
// partner's edits appear within seconds (NFR-18). Same origin, so the session cookie authenticates it.
import { ServerMessage } from '@pf/api-types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export function useLive(enabled: boolean): void {
  const queries = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let socket: WebSocket | null = null;
    let delay = 1000;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Only synced data changes on a nudge (never `me` or the map catalog), and only what's on
    // screen refetches; the rest is marked stale. The server doesn't nudge us about our own pushes.
    const refresh = () => {
      void queries.invalidateQueries({ queryKey: ['bundle'] });
      void queries.invalidateQueries({ queryKey: ['trips'] });
    };
    const connect = () => {
      socket = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/v1/live`);
      socket.onopen = () => {
        delay = 1000;
        refresh(); // catch up on anything missed while disconnected
      };
      socket.onmessage = (event) => {
        const message = ServerMessage.safeParse(JSON.parse(String(event.data)));
        if (!message.success) return;
        if (message.data.t === 'cursor') refresh();
        if (message.data.t === 'bye') stopped = true;
      };
      socket.onclose = () => {
        if (stopped) return;
        timer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 120_000); // each attempt costs Worker + Durable Object requests
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      socket?.close();
    };
  }, [enabled, queries]);
}
